import React, { useEffect, useState } from "react";
import { Spin, Empty, Modal, Button, Segmented, Badge, Alert } from "antd";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import ProductListCard from "../Product/ProductListCard";
import ProductDetail from "../Product/ProductDetail";
import "../Common/CollectionPage.css";
import "../Product/ProductListCard.css";
import "./OnlineSellerProducts.css";

// "dd-MM-yyyy"
const formatDate = (dateString) => {
  if (!dateString) return "";
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}-${month}-${date.getFullYear()}`;
};

// Continuing products online sellers can list on their own stores and reorder, plus the recently
// discontinued ones they should take down. Only users marked Online Seller get data (API returns 403).
const OnlineSellerProducts = ({ refreshCartCount }) => {
  const navigate = useNavigate();
  const [data, setData] = useState({ Available: [], AvailableCount: 0, Discontinued: [], DiscontinuedVisibleDays: 90 });
  const [loading, setLoading] = useState(true);
  const [notSeller, setNotSeller] = useState(false);
  const [tab, setTab] = useState("available");
  const [selectedCategories, setSelectedCategories] = useState(null); // null = All
  const [showProductModal, setShowProductModal] = useState(false);
  const [selectedProductId, setSelectedProductId] = useState(null);

  useEffect(() => {
    async function fetchList() {
      setLoading(true);
      try {
        const tokenString = localStorage.getItem("token");
        const response = await axios.get(
          global.config.APIURL.BaseURL + "api/OnlineSellerProduct/GetMyOnlineSellerProducts",
          { headers: { Authorization: "Bearer " + tokenString } }
        );
        setData({
          Available: response.data.Available || [],
          AvailableCount: response.data.AvailableCount || 0,
          Discontinued: response.data.Discontinued || [],
          DiscontinuedVisibleDays: response.data.DiscontinuedVisibleDays || 90,
        });
      } catch (error) {
        if (error.response && error.response.status === 403) setNotSeller(true);
      } finally {
        setLoading(false);
      }
    }
    fetchList();
  }, []);

  const allCategoryNames = data.Available.map((c) => c.Category);
  const shownCategories = data.Available.filter(
    (c) => !selectedCategories || selectedCategories.includes(c.Category)
  );

  const toggleCategory = (cat) => {
    const base = selectedCategories || [];
    const next = base.includes(cat) ? base.filter((c) => c !== cat) : [...base, cat];
    setSelectedCategories(next.length ? next : null);
  };

  const handleProductCardClick = (product) => {
    setSelectedProductId(product.ProductID);
    setShowProductModal(true);
  };

  const closeModal = () => {
    setShowProductModal(false);
    setSelectedProductId(null);
  };

  if (loading) {
    return (
      <div style={{ textAlign: "center", padding: 72 }}>
        <Spin size="large" />
      </div>
    );
  }

  if (notSeller) {
    return (
      <div className="collection-page">
        <Empty
          style={{ padding: "64px 0" }}
          description={
            <div>
              <div className="osp-empty-title">This section is only for online sellers.</div>
              <div className="osp-empty-sub">Contact your sales person to get it enabled.</div>
            </div>
          }
        >
          <Button type="primary" onClick={() => navigate("/")}>
            Browse catalogue
          </Button>
        </Empty>
      </div>
    );
  }

  return (
    <div className="collection-page">
      <div className="collection-header">
        <h1>Online Seller Products</h1>
        <p>Continuing designs: safe to list online and reorder any time</p>
      </div>

      <div className="osp-tabs">
        <Segmented
          size="large"
          value={tab}
          onChange={setTab}
          options={[
            { value: "available", label: `Available (${data.AvailableCount})` },
            {
              value: "discontinued",
              label: (
                <span>
                  Discontinued{" "}
                  <Badge count={data.Discontinued.length} size="small" style={{ backgroundColor: "#ff4d4f" }} />
                </span>
              ),
            },
          ]}
        />
      </div>

      {tab === "available" ? (
        data.Available.length === 0 ? (
          <Empty description="No products on the online seller list yet" style={{ padding: "64px 0" }} />
        ) : (
          <>
            <div className="collection-chips">
              <button
                type="button"
                className={`collection-chip ${!selectedCategories ? "collection-chip--active" : ""}`}
                onClick={() => setSelectedCategories(null)}
              >
                All
              </button>
              {allCategoryNames.map((cat) => (
                <button
                  key={cat}
                  type="button"
                  className={`collection-chip ${
                    selectedCategories && selectedCategories.includes(cat) ? "collection-chip--active" : ""
                  }`}
                  onClick={() => toggleCategory(cat)}
                >
                  {cat}
                </button>
              ))}
            </div>

            {shownCategories.map((category) => (
              <section className="collection-section" key={category.Category}>
                <div className="collection-section__head">
                  <h2 className="collection-section__title">{category.Category}</h2>
                  <span className="collection-section__rule" />
                  <span className="collection-section__count">{category.Products.length} items</span>
                </div>
                <div className="collection-grid">
                  {category.Products.map((product) => (
                    <ProductListCard
                      key={product.ProductID}
                      product={product}
                      showProdStatus
                      showPrice
                      onClick={handleProductCardClick}
                    />
                  ))}
                </div>
              </section>
            ))}
          </>
        )
      ) : (
        <>
          <Alert
            type="warning"
            showIcon
            style={{ marginBottom: 20 }}
            message="These designs are discontinued and won't be restocked."
            description="Please remove them from your online store listings (Amazon, Meesho, your own website)."
          />
          {data.Discontinued.length === 0 ? (
            <Empty
              description={`Nothing discontinued in the last ${data.DiscontinuedVisibleDays} days`}
              style={{ padding: "48px 0" }}
            />
          ) : (
            <div className="collection-grid">
              {data.Discontinued.map((p) => (
                <div className="product-tile product-tile--disc" key={p.ProductID}>
                  <div className="product-tile__image-wrap">
                    <span className="product-tile__disc-label">DISCONTINUED</span>
                    <img className="product-tile__image" src={p.ImageUrl} alt={p.ProductName} loading="lazy" />
                  </div>
                  <div className="product-tile__body">
                    <h4 className="product-tile__name" title={p.ProductName}>
                      {p.ProductName}
                    </h4>
                    <p className="product-tile__meta">Design No. {p.DesignNo}</p>
                    <div className="product-tile__disc-meta">
                      Removed on {formatDate(p.RemovedOn)}
                      {p.RemovedReason && <div className="product-tile__disc-note">"{p.RemovedReason}"</div>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="osp-footnote">Showing designs removed in the last {data.DiscontinuedVisibleDays} days</div>
        </>
      )}

      {/* Product Detail Modal (same as New Arrivals) */}
      <Modal
        open={showProductModal}
        onCancel={closeModal}
        footer={
          <div style={{ display: "flex", justifyContent: "center", padding: "16px 0 0 0" }}>
            <Button
              type="primary"
              style={{ minWidth: 120, borderRadius: 6, background: "#ff4d4f", borderColor: "#ff4d4f" }}
              onClick={closeModal}
            >
              Close
            </Button>
          </div>
        }
        width="90%"
        style={{ maxWidth: 900 }}
        destroyOnClose
        styles={{ padding: 0 }}
      >
        {showProductModal && selectedProductId && (
          <ProductDetail
            refreshCartCount={refreshCartCount}
            productIdQuery={selectedProductId}
            isModal
            setDocumentTitle={() => {}}
          />
        )}
      </Modal>
    </div>
  );
};

export default OnlineSellerProducts;
